import React, {useEffect} from 'react';
import {BrowserRouter, Route, Routes, useParams, Navigate} from 'react-router-dom';

import {utm} from '../utm/utm';
import Header from '../header/Header';
import Footer from '../header/Footer';
import CookiesPOPUP from '../cookies/CookiesPOPUP';
import {getContestMeta} from './contestMeta';

import * as tracker from '../tracker';
import {getCookie, setCookie} from '../cookies';

const HomepageQuantiacs = React.lazy(() => import('../homepageQuantiacs/HomepageQuantiacs'));
const CookiesQuantiacs = React.lazy(() => import('../cookies/CookiesQuantiacs'));
const TermsOfUse = React.lazy(() => import('../termsofuse/TermsOfUse'));
const PersonalData = React.lazy(() => import('../personaldata/PersonalData'));
const Vacancy = React.lazy(() => import('../career/vacancy/Vacancy'));
const OpenPositions = React.lazy(() => import('../career/openpositions/OpenPositions'));
const FAQ = React.lazy(() => import('../faq/FAQ'));
const Contacts = React.lazy(() => import('../contacts/Contacts'));
const StatisticSubmissionPage = React.lazy(() => import('../statisticSubmission'));
const ContestQuantiacs = React.lazy(() => import('../competition/ContestQuantiacs'));
const FundingLeaderboard = React.lazy(() => import('../leaderboardFunding/FundingLeaderboard'));
const ReferralProgram = React.lazy(
    () => import('../referralProgram/ReferralProgramForAdvertising'),
);
const LeaderboardQ15 = React.lazy(() => import('../leaderboardQuantiacs/LeaderboardQ15'));
const LeaderboardQ16 = React.lazy(() => import('../leaderboardQuantiacs/LeaderboardQ16'));
const LeaderboardQ17 = React.lazy(() => import('../leaderboardQuantiacs/LeaderboardQ17'));
const LeaderboardQ18 = React.lazy(() => import('../leaderboardQuantiacs/LeaderboardQ18'));
const LeaderboardQ19 = React.lazy(() => import('../leaderboardQuantiacs/LeaderboardQ19'));
const LeaderboardQ20 = React.lazy(() => import('../leaderboardQuantiacs/LeaderboardQ20'));
const LeaderboardQ21 = React.lazy(() => import('../leaderboardQuantiacs/LeaderboardQ21'));
const LeaderboardQ22 = React.lazy(() => import('../leaderboardQuantiacs/LeaderboardQ22'));
const LeaderboardQ23 = React.lazy(() => import('../leaderboardQuantiacs/LeaderboardQ23'));
const LeaderboardQ24 = React.lazy(() => import('../leaderboardQuantiacs/LeaderboardQ24'));
const LeaderboardQ25 = React.lazy(() => import('../leaderboardQuantiacs/LeaderboardQ25'));
const ContestQuantiacsFull15 = React.lazy(() => import('../competition/ContestQuantiacsFull15'));
const ContestQuantiacsFull16 = React.lazy(() => import('../competition/ContestQuantiacsFull16'));
const ContestQuantiacsFull17 = React.lazy(() => import('../competition/ContestQuantiacsFull17'));
const ContestQuantiacsFull18 = React.lazy(() => import('../competition/ContestQuantiacsFull18'));
const ContestQuantiacsFull19 = React.lazy(() => import('../competition/ContestQuantiacsFull19'));
const ContestQuantiacsFull20 = React.lazy(() => import('../competition/ContestQuantiacsFull20'));
const ContestQuantiacsFull21 = React.lazy(() => import('../competition/ContestQuantiacsFull21'));
const ContestQuantiacsFull22 = React.lazy(() => import('../competition/ContestQuantiacsFull22'));
const ContestQuantiacsFull23 = React.lazy(() => import('../competition/ContestQuantiacsFull23'));
const ContestQuantiacsFull24 = React.lazy(() => import('../competition/ContestQuantiacsFull24'));
const ContestQuantiacsFull25 = React.lazy(() => import('../competition/ContestQuantiacsFull25'));
const NotFound = React.lazy(() => import('../404/NotFound'));

const leaderboardComponents = {
    15: LeaderboardQ15,
    16: LeaderboardQ16,
    17: LeaderboardQ17,
    18: LeaderboardQ18,
    19: LeaderboardQ19,
    20: LeaderboardQ20,
    21: LeaderboardQ21,
    22: LeaderboardQ22,
    23: LeaderboardQ23,
    24: LeaderboardQ24,
    25: LeaderboardQ25,
};

const contestComponents = {
    15: ContestQuantiacsFull15,
    16: ContestQuantiacsFull16,
    17: ContestQuantiacsFull17,
    18: ContestQuantiacsFull18,
    19: ContestQuantiacsFull19,
    20: ContestQuantiacsFull20,
    21: ContestQuantiacsFull21,
    22: ContestQuantiacsFull22,
    23: ContestQuantiacsFull23,
    24: ContestQuantiacsFull24,
    25: ContestQuantiacsFull25,
};

function ContestDynamicRoute() {
    const {ContestNumber} = useParams();
    const ContestComponent = contestComponents[ContestNumber];
    return ContestComponent ? <ContestComponent /> : <NotFound />;
}

function LeaderboardDynamicRoute() {
    const {ContestNumber} = useParams();
    const LeaderboardComponent = leaderboardComponents[ContestNumber];

    if (!LeaderboardComponent) {
        return <NotFound />;
    }

    const meta = getContestMeta(ContestNumber);

    return (
        <>
            <title>{meta.title}</title>
            <meta name="description" content={meta.description} />
            <link rel="canonical" href={meta.canonical} />
            <LeaderboardComponent />
        </>
    );
}

function App() {
    useEffect(() => {
        const utmName = 'utm_is_exist';
        if (!isUtmExist(utmName)) {
            setUtmInCookieWithoutReferral(utmName);
        }
        setReferral();
        const qn_uid = getCookie('qn_uid');
        tracker.userInfo({qntId: qn_uid});

        function isUtmExist(name) {
            const currentUtm = getCookie(name);
            return currentUtm !== '' && currentUtm !== 'undefined';
        }

        function setUtmInCookieWithoutReferral(name) {
            const metrics = utm(window.location.href, '&');
            for (const [key, value] of Object.entries(metrics)) {
                if (!(key === 'utm_source' && value === 'reference')) {
                    setCookie(key, value, 800000);
                }
            }
            setCookie(name, 'exist', 800000);
        }

        function setReferral() {
            const metrics = utm(window.location.href, '&');
            if (metrics.utm_source && metrics.utm_source === 'reference') {
                const utmSource = getCookie('utm_source');
                if (utmSource !== 'reference') {
                    tracker.event('REFERRAL_PROGRAM_OPEN_REFERRAL_LINK_FIRST');
                }
                setCookie('utm_medium', metrics.utm_medium, 800000);
                setCookie('utm_source', metrics.utm_source, 800000);
                tracker.event('REFERRAL_PROGRAM_OPEN_REFERRAL_LINK_TOTAL');
            }
        }
    }, []);

    return (
        <BrowserRouter>
            <div className="wrapper">
                <Header />

                <React.Suspense
                    fallback={(
                        <main className="main_wrapper">
                            <div
                                className="route-loading-placeholder"
                                style={{minHeight: '60vh'}}
                            />
                        </main>
                    )}
                >
                    <main className="main_wrapper">
                        <Routes>
                            <Route path="/" element={<HomepageQuantiacs />} />
                            <Route path="/leaderboard" element={<FundingLeaderboard />} />
                            <Route
                                path="/leaderboard/:ContestNumber"
                                element={<LeaderboardDynamicRoute />}
                            />
                            <Route path="/contest" element={<ContestQuantiacs />} />
                            <Route path="/contest/:ContestNumber" element={<ContestDynamicRoute />} />
                            <Route path="/career" element={<OpenPositions />} />
                            <Route path="/career/openpositions" element={<OpenPositions />} />
                            <Route path="/career/openpositions/:id" element={<Vacancy />} />
                            <Route path="/termsofuse" element={<TermsOfUse />} />
                            <Route path="/personalinfo" element={<PersonalData />} />
                            <Route path="/cookies" element={<CookiesQuantiacs />} />
                            <Route
                                path="/statistic/:submissionId"
                                element={<StatisticSubmissionPage />}
                            />
                            <Route path="/faq" element={<FAQ />} />
                            <Route path="/contacts" element={<Contacts />} />
                            <Route path="/contact" element={<Navigate to="/contacts" replace />} />
                            <Route path="/referral" element={<ReferralProgram />} />
                            <Route path="*" element={<NotFound />} />
                        </Routes>
                        <CookiesPOPUP />
                    </main>
                    <Footer />
                </React.Suspense>
            </div>
        </BrowserRouter>
    );
}

export default App;
